import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { compressImage, getInitials } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Image, X, Loader2, Sparkles, Video, Camera } from 'lucide-react';
import { VideoFilterEditor, ImageFilterEditor } from '../media/VideoFilter';
import CameraRecorder from '../media/CameraRecorder';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

export const CreatePostModal = ({ open, onClose }) => {
  const { user, token, language } = useAuth();
  const tr = t(language);
  const [content, setContent] = useState('');
  const [media, setMedia] = useState([]);
  const [mediaType, setMediaType] = useState('none');
  const [mediaFilter, setMediaFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFilterEditor, setShowFilterEditor] = useState(false);
  const [rawMedia, setRawMedia] = useState(null);
  const [rawMediaFile, setRawMediaFile] = useState(null);
  const [showCameraRecorder, setShowCameraRecorder] = useState(false);
  const fileInputRef = useRef(null);

  const handleMediaSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const file = files[0];
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      toast.error('Format not supported');
      return;
    }

    // Check file size
    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      toast.error('Video too large. Maximum 100MB allowed.');
      return;
    }
    if (isImage && file.size > MAX_IMAGE_SIZE) {
      toast.error('Image too large. Maximum 10MB allowed.');
      return;
    }

    try {
      if (isImage) {
        const compressed = await compressImage(file);
        setRawMedia(compressed);
        setRawMediaFile(file);
        setMediaType('image');
        setShowFilterEditor(true);
      } else {
        // For video, create object URL for preview
        const objectUrl = URL.createObjectURL(file);
        setRawMedia(objectUrl);
        setRawMediaFile(file);
        setMediaType('video');
        setShowFilterEditor(true);
      }
    } catch (error) {
      toast.error('Error processing media');
    }
  };

  const uploadMediaFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Don't set Content-Type header - let browser set it with correct boundary
      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });
      
      console.log('Upload successful:', response.data);
      return response.data.url;
    } catch (error) {
      console.error('Upload failed:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Upload failed. Please try again.');
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleFilterSave = async (filterData) => {
    // For videos, we need to upload the file first
    if (mediaType === 'video' && rawMediaFile) {
      try {
        const uploadedUrl = await uploadMediaFile(rawMediaFile);
        setMedia([uploadedUrl]);
      } catch (error) {
        toast.error('Failed to upload video. Please try again.');
        return;
      }
    } else {
      setMedia([filterData.originalSrc]);
    }
    setMediaFilter(filterData.filter);
    setShowFilterEditor(false);
  };

  const handleFilterCancel = () => {
    setShowFilterEditor(false);
    setRawMedia(null);
    setRawMediaFile(null);
    setMediaType('none');
  };

  const handleCameraRecordComplete = async (file, previewUrl, filterCss) => {
    setShowCameraRecorder(false);
    setRawMediaFile(file);
    setRawMedia(previewUrl);
    setMediaType('video');
    setMediaFilter(filterCss !== 'none' ? filterCss : '');
    
    // Upload the recorded video
    try {
      const uploadedUrl = await uploadMediaFile(file);
      setMedia([uploadedUrl]);
    } catch (error) {
      toast.error('Failed to upload video. Please try again.');
      setRawMediaFile(null);
      setRawMedia(null);
      setMediaType('none');
    }
  };

  const handleCameraCancel = () => {
    setShowCameraRecorder(false);
  };

  const removeMedia = () => {
    setMedia([]);
    setMediaType('none');
    setMediaFilter('');
    setRawMedia(null);
    setRawMediaFile(null);
  };

  const handleSubmit = async () => {
    if (!content.trim() && media.length === 0) {
      toast.error('Write something or add a photo');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/posts`, {
        content: content.trim(),
        media,
        media_type: mediaType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(tr('posted'));
      setContent('');
      setMedia([]);
      setMediaType('none');
      setMediaFilter('');
      onClose();
      
      // Reload page to show new post
      window.location.reload();
    } catch (error) {
      toast.error(tr('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !uploading) {
      setContent('');
      setMedia([]);
      setMediaType('none');
      setMediaFilter('');
      setRawMedia(null);
      setRawMediaFile(null);
      onClose();
    }
  };

  // Show camera recorder
  if (showCameraRecorder) {
    return (
      <CameraRecorder
        onRecordComplete={handleCameraRecordComplete}
        onCancel={handleCameraCancel}
      />
    );
  }

  // Show filter editor
  if (showFilterEditor && rawMedia) {
    if (mediaType === 'video') {
      return (
        <VideoFilterEditor
          videoSrc={rawMedia}
          onSave={handleFilterSave}
          onCancel={handleFilterCancel}
        />
      );
    } else {
      return (
        <ImageFilterEditor
          imageSrc={rawMedia}
          onSave={handleFilterSave}
          onCancel={handleFilterCancel}
        />
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="create-post-modal">
        <DialogHeader>
          <DialogTitle>{tr('newPost')}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-3 mt-4">
          <Avatar className="w-12 h-12 ring-2 ring-primary/20">
            <AvatarImage src={user?.avatar} alt={user?.display_name} />
            <AvatarFallback className="bg-primary/10 text-primary">
              {getInitials(user?.display_name || user?.username)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1">
            <Textarea
              placeholder={tr('whatsHappening')}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[120px] resize-none border-none bg-transparent text-lg focus-visible:ring-0 p-0"
              data-testid="post-content-input"
            />

            {/* Media Preview */}
            {media.length > 0 && (
              <div className="relative mt-3 rounded-2xl overflow-hidden">
                {mediaType === 'image' && (
                  <img 
                    src={media[0]} 
                    alt="Preview" 
                    className="w-full max-h-[300px] object-cover" 
                    style={{ filter: mediaFilter }}
                  />
                )}
                {mediaType === 'video' && (
                  <video 
                    src={media[0].startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${media[0]}` : media[0]} 
                    className="w-full max-h-[300px]" 
                    style={{ filter: mediaFilter }}
                    controls 
                  />
                )}
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="rounded-full bg-background/80 backdrop-blur-sm"
                    onClick={() => {
                      setRawMedia(media[0]);
                      setShowFilterEditor(true);
                    }}
                    data-testid="edit-filter-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="rounded-full bg-background/80 backdrop-blur-sm"
                    onClick={removeMedia}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                {mediaFilter && (
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-xs rounded-full">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    Filter applied
                  </div>
                )}
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="mt-3 p-4 rounded-2xl bg-secondary">
                <div className="flex items-center gap-3">
                  <Video className="w-5 h-5 text-primary animate-pulse" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Uploading video...</p>
                    <div className="mt-2 h-2 bg-background rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{uploadProgress}%</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/50">
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*,video/*"
                  ref={fileInputRef}
                  onChange={handleMediaSelect}
                  className="hidden"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full text-primary hover:bg-primary/10"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || uploading}
                  data-testid="add-media-btn"
                  title="Add from gallery"
                >
                  <Image className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full text-pink-500 hover:bg-pink-500/10"
                  onClick={() => setShowCameraRecorder(true)}
                  disabled={loading || uploading}
                  data-testid="record-video-btn"
                  title="Record video"
                >
                  <Camera className="w-5 h-5" />
                </Button>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={loading || uploading || (!content.trim() && media.length === 0)}
                className="rounded-full px-6 font-bold"
                data-testid="submit-post-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  tr('post')
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePostModal;
